import React, { useContext, useMemo, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import './Sidebar.css'
import { UserContext } from '../../../App'
import 'simplebar-react/dist/simplebar.min.css'
import SideBarDivider from './Divider'
import SidebarNavItem from './NavItem'
import SidebarGroup from './SidebarGroup'

import Drawer from '@mui/material/Drawer'
import HomeIcon from '@mui/icons-material/Home'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'

import { SiteModeContext } from '../MainFrame'
import { Link, useLocation } from 'react-router-dom'
import { SidebarSearchButton } from '../search/GlobalSearch'
import { SECTIONS, SUBCONTRACTOR_MENU } from '../../../navigation/sections'
import { getEntryById } from '../../../navigation/registry'
import { getNavIcon } from '../../../navigation/icons'
import { getNavAccess } from '../../../navigation/visibility'
import { useNavContext } from '../../../navigation/useNavigation'
import { getRecentPages } from '../../../navigation/recents'
import { useNavCounts } from '../NavCountsContext'
import API from '../../../services/API'

// The sidebar is a renderer over the navigation registry
// (src/navigation/registry.js + sections.js): item order, grouping, icons and
// visibility all live there. This file only handles chrome - collapse state,
// favourites/recents, the mobile drawer - so new pages never touch it.

// Accent colours per section (Sidebar.css)
const SECTION_CSS = {
  myWork: 'sidebar_section_work',
  workforce: 'sidebar_section_workforce',
  people: 'sidebar_section_people',
  commandControl: 'sidebar_section_command',
  events: 'sidebar_section_events',
  patientTransport: 'sidebar_section_transport',
  fleetEquipment: 'sidebar_section_fleet',
  patientRecords: 'sidebar_section_patient_records',
  governance: 'sidebar_section_governance',
  clinical: 'sidebar_section_clinical',
  customers: 'sidebar_section_customers',
  finance: 'sidebar_section_finance',
  system: 'sidebar_section_system',
  help: 'sidebar_section_help',
}

const Sidebar = () => {
  const { sidebarState, setSidebarState } = useContext(SiteModeContext)
  const { collapsed, mobileOpen } = sidebarState
  const { user, setUser } = useContext(UserContext)

  // Which sections are expanded. Default open; persisted per user so the
  // layout survives reloads and devices.
  const [openGroups, setOpenGroups] = useState(
    () => user?.uiPreferences?.sidebarOpenGroups ?? {},
  )
  const saveTimer = useRef(null)

  const toggleGroup = (sectionId) => {
    setOpenGroups((prev) => {
      const next = { ...prev, [sectionId]: !(prev[sectionId] ?? true) }
      clearTimeout(saveTimer.current)
      saveTimer.current = setTimeout(() => {
        API.post('/account/uipreferences', { sidebarOpenGroups: next })
          .then((res) => {
            if (res.status === 200) {
              setUser((prevUser) => ({ ...prevUser, uiPreferences: res.data }))
            }
          })
          .catch(() => {})
      }, 1000)
      return next
    })
  }

  const toggleDrawer = (event) => {
    if (
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return
    }
    setSidebarState((prev) => ({ ...prev, mobileOpen: false }))
  }

  const frameProps = { openGroups, toggleGroup }

  return (
    <React.Fragment>
      <Box
        sx={{
          display: {
            xs: 'none',
            lg: 'flex',
          },
        }}
      >
        <SideBarFrame collapsed={collapsed} {...frameProps} />
      </Box>
      <Box
        sx={{
          display: {
            xs: 'flex',
            lg: 'none',
          },
        }}
      >
        <Drawer
          anchor="left"
          open={mobileOpen}
          onClose={toggleDrawer}
          onClick={toggleDrawer}
          sx={{
            display: {
              xs: 'flex',
              lg: 'none',
            },
          }}
        >
          <SideBarFrame mobileView collapsed={collapsed} {...frameProps} />
        </Drawer>
      </Box>
    </React.Fragment>
  )
}

// Compact rows for the favourites/recents blocks - these aren't registry nav
// items (favourites can point at any path), so they don't use SidebarNavItem.
const QuickRow = ({ to, label, icon }) => (
  <Link to={to} className="sidebar_navitem">
    <Stack
      direction="row"
      flex={1}
      spacing={1}
      paddingY={0.25}
      paddingX={1}
      sx={{ alignItems: 'center', minWidth: 0 }}
    >
      {icon}
      <Box
        fontSize={13}
        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {label}
      </Box>
    </Stack>
  </Link>
)

const SideBarFrame = ({ collapsed, mobileView, openGroups, toggleGroup }) => {
  const { user } = useContext(UserContext)
  const ctx = useNavContext()
  const { counts } = useNavCounts()
  const location = useLocation()
  const orgSettings = ctx.orgSettings

  const favourites = user?.uiPreferences?.favouritePages ?? []
  // Re-read on navigation: the tracker in MainFrame has just recorded the
  // new page, and the list is tiny.
  const recents = useMemo(
    () =>
      getRecentPages(user?._id).filter(
        (entry) => getNavAccess(entry, ctx) !== 'hidden' || !entry.nav,
      ),
    [user?._id, location.pathname, ctx],
  )

  const homeItems = SECTIONS.find((section) => section.id === 'home')
    ?.items ?? []

  return (
    <Box
      sx={{
        width: collapsed ? '80px' : '240px',
        zIndex: 1001,
      }}
      flexDirection="column"
      display="flex"
      gap={0}
      alignItems="center"
      height="100%"
      maxHeight="100vh"
      overflow="hidden"
      className="sidebar_frame"
    >
      {orgSettings?.logo && (
        <Link to="/" aria-label="Home">
          <Box display="flex" justifyContent="center" maxHeight="80px">
            <Box
              component="img"
              src={orgSettings?.logo}
              sx={{
                objectFit: 'contain',
                backgroundColor:
                  orgSettings?.logobackgroundColor ?? 'primary.main',
              }}
              maxWidth="100%"
              bgcolor="whitesmoke"
              padding={1}
              borderRadius={1}
            />
          </Box>
        </Link>
      )}
      {mobileView && <SidebarSearchButton />}
      <Stack
        width="100%"
        spacing={0}
        minHeight={0}
        overflow="auto"
        paddingBottom={4}
        className="scrollable sidebar_menu"
      >
        {ctx.isSubcontractor ? (
          <>
            <Box className="sidebar_home_item">
              <SidebarNavItem
                icon={<HomeIcon />}
                title="Home"
                url="/"
                end
                suppressActiveStyle
              />
            </Box>
            <Box className="sidebar_section sidebar_section_clinical">
              <SideBarDivider
                text={SUBCONTRACTOR_MENU.title}
                sideBarCollapsed={collapsed}
              />
              {SUBCONTRACTOR_MENU.items
                .map((id) => getEntryById(id))
                .filter(Boolean)
                .map((entry) => (
                  <SidebarNavItem
                    key={entry.id}
                    icon={getNavIcon(entry.icon)}
                    title={entry.nav?.title ?? entry.title}
                    url={entry.path}
                    end={entry.nav?.end}
                  />
                ))}
            </Box>
          </>
        ) : (
          <>
            {homeItems
              .map((id) => getEntryById(id))
              .filter(
                (entry) => entry && getNavAccess(entry, ctx) === 'visible',
              )
              .map((entry) => (
                <Box key={entry.id} className="sidebar_home_item">
                  <SidebarNavItem
                    icon={getNavIcon(entry.icon)}
                    title={entry.nav?.title ?? entry.title}
                    url={entry.path}
                    end={entry.nav?.end}
                    suppressActiveStyle={entry.nav?.suppressActiveStyle}
                  />
                </Box>
              ))}

            {!collapsed && favourites.length > 0 && (
              <Box className="sidebar_section sidebar_section_start">
                <SideBarDivider text="Favourites" sideBarCollapsed={collapsed} />
                {favourites.map((fav) => (
                  <QuickRow
                    key={fav.path}
                    to={fav.path}
                    label={fav.label}
                    icon={
                      <StarRoundedIcon
                        sx={{ fontSize: '16px !important', color: '#CCA43B' }}
                      />
                    }
                  />
                ))}
              </Box>
            )}

            {!collapsed && recents.length > 0 && (
              <Box className="sidebar_section sidebar_section_start">
                <SideBarDivider text="Recent" sideBarCollapsed={collapsed} />
                {recents.slice(0, 5).map((entry) => (
                  <QuickRow
                    key={entry.id}
                    to={entry.path}
                    label={entry.nav?.title ?? entry.title}
                    icon={
                      <HistoryRoundedIcon sx={{ fontSize: '16px !important' }} />
                    }
                  />
                ))}
              </Box>
            )}

            {SECTIONS.filter(
              (section) => !section.bare && section.id !== 'clinical',
            ).map((section) => (
              <SidebarGroup
                key={section.id}
                section={{ ...section, cssClass: SECTION_CSS[section.id] }}
                ctx={ctx}
                counts={counts}
                collapsed={collapsed}
                open={openGroups[section.id] ?? true}
                onToggle={toggleGroup}
              />
            ))}
          </>
        )}

        {ctx.isSubcontractor && (
          <SidebarGroup
            section={{
              ...SECTIONS.find((section) => section.id === 'help'),
              cssClass: SECTION_CSS.help,
            }}
            ctx={ctx}
            counts={counts}
            collapsed={collapsed}
            open
            onToggle={() => {}}
          />
        )}
      </Stack>
    </Box>
  )
}

export default Sidebar
